import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { ApiGetParams, ApiRequestOptions, ApiService } from '../../core/transport/api.service';
import { Endpoints } from '../../core/transport/endpoints';
import { collectPages } from '../../core/transport/collect-pages';
import { IPaginated } from '../../core/types/http.interface';
import {
  CustomerSiteRequestAdminFilter,
  ICustomerSiteRequest,
  ICustomerSiteRequestCreatePayload,
  ICustomerSiteRequestDecisionPayload,
} from '../../core/types/customer-site-request.interface';
import {
  ICustomer,
  ICustomerCreatePayload,
  ICustomerMergePayload,
  ICustomerMergeResult,
  ICustomerProfile,
  ICustomerProfileActivity,
  ICustomerProfileActivityPayload,
  ICustomerProfileAdminQueueItem,
  ICustomerProfileAnalytics,
  ICustomerProfileAuditEvent,
  ICustomerProfileApprovalSlaReminderPayload,
  ICustomerProfileComplaint,
  ICustomerProfileComplaintDecisionPayload,
  ICustomerProfileComplaintPayload,
  ICustomerProfileComplaintResponsePayload,
  ICustomerProfileComplianceSummary,
  ICustomerProfileCreatePayload,
  ICustomerProfileDecisionPayload,
  ICustomerProfileMoreInfoPayload,
  ICustomerProfilePricingPayload,
  ICustomerProfileRenewal,
  ICustomerProfileRenewalDecisionPayload,
  ICustomerProfileRenewalPayload,
  ICustomerProfileRevokePayload,
  ICustomerProfileStatusRequest,
  ICustomerProfileStatusRequestCureNoticePayload,
  ICustomerProfileStatusRequestPayload,
  ICustomerProfileStatusUpdate,
  ICustomerProfileStatusUpdatePayload,
  ICustomerProfileTerminateResellerPayload,
  ICustomerProfileTerminateResellerResult,
  ICustomerProfileTransferPayload,
  ICustomerProfileUpdatePayload,
} from '../../core/types/customer-profile.interface';
import { IPartNumber } from '../../core/types/part-number.interface';

@Injectable({
  providedIn: 'root',
})
export class CustomerProfilesService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  findMany(params: ApiGetParams = {},
    options?: ApiRequestOptions,
  ): Observable<IPaginated<ICustomerProfile[]>> {
    return this.api.get<IPaginated<ICustomerProfile[]>>(this.listEndpoint, params, options);
  }

  findApprovedRegistrations(params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfile[]>> {
    const approvedParams = params instanceof HttpParams
      ? params.set('filter[status]', params.get('filter[status]') ?? 'approved')
      : { 'filter[status]': 'approved', ...params };
    const paginated = params instanceof HttpParams
      ? params.has('page') || params.has('limit')
      : 'page' in params || 'limit' in params;
    if (paginated) return this.findMany(approvedParams, options);
    return collectPages((page, limit) => this.findMany(
      approvedParams instanceof HttpParams
      ? approvedParams.set('page', page).set('limit', limit)
      : { ...approvedParams, page, limit },
      options,
    ));
  }

  findOne(customerProfileId: string, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.get<ICustomerProfile>(
      Endpoints.customerProfiles.one(customerProfileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      {},
      options,
    );
  }

  createOne(payload: ICustomerProfileCreatePayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.post<ICustomerProfile>(this.listEndpoint, payload, options);
  }

  updateOne(customerProfileId: string, payload: ICustomerProfileUpdatePayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.put<ICustomerProfile>(Endpoints.customerProfiles.one(customerProfileId, true), payload, options);
  }

  decideOne(customerProfileId: string, payload: ICustomerProfileDecisionPayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminDecision(customerProfileId), payload, options);
  }

  updatePricing(customerProfileId: string, payload: ICustomerProfilePricingPayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.put<ICustomerProfile>(Endpoints.customerProfiles.adminPricing(customerProfileId), payload, options);
  }

  deleteOne(customerProfileId: string, options?: ApiRequestOptions): Observable<void> {
    return this.api.delete<void>(Endpoints.customerProfiles.one(customerProfileId, true), options);
  }

  findPricingPartNumbers(options?: ApiRequestOptions): Observable<IPartNumber[]> {
    return this.api.get<IPartNumber[]>(Endpoints.partNumbers.adminList, {}, options);
  }

  // Customers

  searchCustomers(query = '', limit = 20, options?: ApiRequestOptions): Observable<ICustomer[]> {
    return this.api.get<ICustomer[]>(this.customersSearchEndpoint, {
      q: query,
      limit,
    }, options);
  }

  createCustomer(payload: ICustomerCreatePayload, options?: ApiRequestOptions): Observable<ICustomer> {
    return this.api.post<ICustomer>(Endpoints.customers.create, payload, options);
  }

  mergeAdminCustomer(customerId: string, payload: ICustomerMergePayload, options?: ApiRequestOptions): Observable<ICustomerMergeResult> {
    return this.api.post<ICustomerMergeResult>(Endpoints.customers.adminMerge(customerId), payload, options);
  }

  // Admin queue and analytics

  getAdminQueue(params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfileAdminQueueItem[]>> {
    return this.api.get<IPaginated<ICustomerProfileAdminQueueItem[]>>(
      Endpoints.customerProfiles.adminQueue,
      params, options);
  }

  getAdminQueueItem(profileId: string, options?: ApiRequestOptions): Observable<ICustomerProfileAdminQueueItem> {
    return this.api.get<ICustomerProfileAdminQueueItem>(Endpoints.customerProfiles.adminQueueOne(profileId), {}, options);
  }

  getAdminAnalytics(params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<ICustomerProfileAnalytics> {
    return this.api.get<ICustomerProfileAnalytics>(Endpoints.customerProfiles.adminAnalytics, params, options);
  }

  // Compliance

  getCompliance(profileId: string, options?: ApiRequestOptions): Observable<ICustomerProfileComplianceSummary> {
    return this.api.get<ICustomerProfileComplianceSummary>(
      Endpoints.customerProfiles.compliance(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      {},
      options,
    );
  }

  getAuditTrail(profileId: string, options?: ApiRequestOptions): Observable<ICustomerProfileAuditEvent[]> {
    return this.api
      .get<IPaginated<ICustomerProfileAuditEvent[]>>(
        Endpoints.customerProfiles.audit(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
        { limit: 100 },
        options,
      )
      .pipe(map((response: IPaginated<ICustomerProfileAuditEvent[]>) => response.items));
  }

  // Activities

  getActivities(profileId: string, params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfileActivity[]>> {
    return this.api.get<IPaginated<ICustomerProfileActivity[]>>(
      Endpoints.customerProfiles.activities(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      params,
      options,
    );
  }

  createActivity(
    profileId: string,
    payload: ICustomerProfileActivityPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileActivity> {
    return this.api.post<ICustomerProfileActivity>(
      Endpoints.customerProfiles.activities(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      payload,
      options,
    );
  }

  updateActivity(
    profileId: string,
    activityId: string,
    payload: ICustomerProfileActivityPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileActivity> {
    return this.api.patch<ICustomerProfileActivity>(
      Endpoints.customerProfiles.activity(profileId, activityId, this.authService.can('customerProfilesAdmin', 'runtime')),
      payload,
      options,
    );
  }

  deleteActivity(profileId: string, activityId: string, options?: ApiRequestOptions): Observable<void> {
    return this.api.delete<void>(
      Endpoints.customerProfiles.activity(profileId, activityId, this.authService.can('customerProfilesAdmin', 'runtime')),
      options,
    );
  }

  // Status Updates

  getStatusUpdates(profileId: string, params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfileStatusUpdate[]>> {
    return this.api.get<IPaginated<ICustomerProfileStatusUpdate[]>>(
      Endpoints.customerProfiles.statusUpdates(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      params,
      options,
    );
  }

  createStatusUpdate(
    profileId: string,
    payload: ICustomerProfileStatusUpdatePayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileStatusUpdate> {
    return this.api.post<ICustomerProfileStatusUpdate>(
      Endpoints.customerProfiles.statusUpdates(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      payload,
      options,
    );
  }

  // Status Requests

  getStatusRequests(
    profileId: string,
    params: ApiGetParams = {},
    options?: ApiRequestOptions,
  ): Observable<IPaginated<ICustomerProfileStatusRequest[]>> {
    return this.api.get<IPaginated<ICustomerProfileStatusRequest[]>>(
      Endpoints.customerProfiles.adminStatusRequests(profileId),
      params, options);
  }

  createStatusRequest(
    profileId: string,
    payload: ICustomerProfileStatusRequestPayload = {},
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileStatusRequest> {
    return this.api.post<ICustomerProfileStatusRequest>(
      Endpoints.customerProfiles.adminStatusRequests(profileId),
      payload, options);
  }

  issueStatusRequestCureNotice(
    profileId: string,
    statusRequestId: string,
    payload: ICustomerProfileStatusRequestCureNoticePayload = {},
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileStatusRequest> {
    return this.api.patch<ICustomerProfileStatusRequest>(
      Endpoints.customerProfiles.adminStatusRequestCureNotice(profileId, statusRequestId),
      payload, options);
  }

  // Renewals

  getRenewals(profileId: string, params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfileRenewal[]>> {
    return this.api.get<IPaginated<ICustomerProfileRenewal[]>>(
      Endpoints.customerProfiles.renewals(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      params,
      options,
    );
  }

  createRenewal(
    profileId: string,
    payload: ICustomerProfileRenewalPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileRenewal> {
    return this.api.post<ICustomerProfileRenewal>(
      Endpoints.customerProfiles.renewals(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      payload,
      options,
    );
  }

  decideRenewal(
    profileId: string,
    renewalId: string,
    payload: ICustomerProfileRenewalDecisionPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileRenewal> {
    return this.api.patch<ICustomerProfileRenewal>(
      Endpoints.customerProfiles.adminRenewalDecision(profileId, renewalId),
      payload,
      options,
    );
  }

  // Complaints

  getComplaints(profileId: string, params: ApiGetParams = {}, options?: ApiRequestOptions): Observable<IPaginated<ICustomerProfileComplaint[]>> {
    return this.api.get<IPaginated<ICustomerProfileComplaint[]>>(
      Endpoints.customerProfiles.complaints(profileId, this.authService.can('customerProfilesAdmin', 'runtime')),
      params,
      options,
    );
  }

  createComplaint(profileId: string, payload: ICustomerProfileComplaintPayload, options?: ApiRequestOptions): Observable<ICustomerProfileComplaint> {
    return this.api.post<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.complaints(profileId, true),
      payload, options);
  }

  respondComplaint(
    profileId: string,
    complaintId: string,
    payload: ICustomerProfileComplaintResponsePayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileComplaint> {
    return this.api.patch<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.complaintResponse(profileId, complaintId),
      payload, options);
  }

  decideComplaint(
    profileId: string,
    complaintId: string,
    payload: ICustomerProfileComplaintDecisionPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileComplaint> {
    return this.api.patch<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.adminComplaintDecision(profileId, complaintId),
      payload, options);
  }

  // Admin actions

  requestMoreInfo(profileId: string, payload: ICustomerProfileMoreInfoPayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminMoreInfo(profileId), payload, options);
  }

  transferProfile(profileId: string, payload: ICustomerProfileTransferPayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminTransfer(profileId), payload, options);
  }

  revokeProfile(profileId: string, payload: ICustomerProfileRevokePayload, options?: ApiRequestOptions): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminRevoke(profileId), payload, options);
  }

  notifyApprovalSlaBreached(
    profileId: string,
    payload: ICustomerProfileApprovalSlaReminderPayload = {},
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfile> {
    return this.api.post<ICustomerProfile>(
      Endpoints.customerProfiles.adminApprovalSlaReminder(profileId),
      payload, options);
  }

  terminateReseller(
    payload: ICustomerProfileTerminateResellerPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerProfileTerminateResellerResult> {
    return this.api.post<ICustomerProfileTerminateResellerResult>(
      Endpoints.customerProfiles.adminTerminateReseller,
      payload, options);
  }

  // Customer site requests

  getSiteRequests(options?: ApiRequestOptions): Observable<ICustomerSiteRequest[]> {
    return this.api.get<ICustomerSiteRequest[]>(Endpoints.customerProfiles.siteRequests(), {}, options);
  }

  createSiteRequest(
    payload: ICustomerSiteRequestCreatePayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerSiteRequest> {
    return this.api.post<ICustomerSiteRequest>(Endpoints.customerProfiles.siteRequests(), payload, options);
  }

  getAdminSiteRequests(status: CustomerSiteRequestAdminFilter = 'pending', options?: ApiRequestOptions): Observable<ICustomerSiteRequest[]> {
    return this.api.get<ICustomerSiteRequest[]>(
      Endpoints.customerProfiles.siteRequests(true),
      { status }, options);
  }

  decideAdminSiteRequest(
    requestId: string,
    payload: ICustomerSiteRequestDecisionPayload,
    options?: ApiRequestOptions,
  ): Observable<ICustomerSiteRequest> {
    return this.api.patch<ICustomerSiteRequest>(
      Endpoints.customerProfiles.adminSiteRequestDecision(requestId),
      payload, options);
  }

  private get customersSearchEndpoint(): string {
    return this.authService.can('customerProfilesAdmin', 'runtime')
      ? Endpoints.customers.adminList
      : Endpoints.customers.search;
  }

  private get listEndpoint(): string {
    return Endpoints.customerProfiles.list(this.authService.can('customerProfilesAdmin', 'runtime'));
  }
}
